import React from 'react';
import {useTranslation} from 'react-i18next';
import {Location} from 'history';
import {useHistory, useLocation} from 'react-router-dom';
import {format} from 'date-fns';
import {useForm} from 'react-hook-form';
import {TFunction} from 'i18next';
import moment from 'moment';

import {PATHS} from 'Routes';
import {getHousingCountryCode, getNumberOfGuests} from 'utils/reservation';
import {useComputedReservationDetails} from 'context/computedReservationDetails';
import {COUNTRY_CODES, GUEST_APP_EVENTS_NAME, HOURS24} from 'utils/constants';

import {valueof} from '../../utils/types';
import Counter from 'components/acommon/Counter';

import Button from 'components/acommon/Button';
import styles from './ConfirmBookingDetailsForm.module.sass';
import useMixPanel from 'hooks/useMixPanel';

type MinMaxInputNumbersType = {
  readonly [key: string]: number;
};

export enum FORM_NAMES {
  children = 'children',
  numberOfGuests = 'number_of_guests',
  timeArrival = 'time_arrival',
  timeLeave = 'time_leave',
}

const MIN_INPUT_NUMBERS: MinMaxInputNumbersType = {
  [FORM_NAMES.children]: 0,
  [FORM_NAMES.numberOfGuests]: 1,
};

const MAX_INPUT_NUMBERS: MinMaxInputNumbersType = {
  [FORM_NAMES.children]: 100,
  [FORM_NAMES.numberOfGuests]: 100,
};

const MIN_CHILDREN_RESTRICTION_AGE: {[key in valueof<typeof COUNTRY_CODES>]: string} = {
  [COUNTRY_CODES.france]: '15',
  [COUNTRY_CODES.spain]: '14',
  [COUNTRY_CODES.usa]: '18',
  [COUNTRY_CODES.uk]: '16',
};

type FormTypes = {
  [FORM_NAMES.numberOfGuests]: number;
  [FORM_NAMES.children]: number;
  [FORM_NAMES.timeArrival]: {label: string; value: {h: number; m: number}};
  [FORM_NAMES.timeLeave]: {label: string; value: {h: number; m: number}};
};

function formatDate(date: Date, country?: string) {
  if (country === COUNTRY_CODES.usa) {
    return format(date, 'MM/dd/yyyy');
  }

  return format(date, 'dd-MM-yyyy');
}

function getFormattedDateOrFallback(date: Date | null = null, country?: string) {
  if (!date) {
    return '';
  }
  return formatDate(date, country);
}

function getPersistedNumberOfGuests(location: Location<FormTypes>): number | null {
  const numberOfGuests = location?.state?.[FORM_NAMES.numberOfGuests];

  if (!numberOfGuests) {
    return null;
  }
  return numberOfGuests;
}

function getInitChildren(location: Location<FormTypes>): number {
  const children = location?.state?.children;

  if (!children) {
    return MIN_INPUT_NUMBERS.children;
  }
  return children;
}

const INIT_DISPLAY_FIELDS = {
  isAdultsFieldVisible: true,
  isChildrenFieldVisible: false,
};
function getDisplayFields(reservation: any = {}) {
  const countryCode = getHousingCountryCode(reservation);

  switch (countryCode) {
    case COUNTRY_CODES.france:
    case COUNTRY_CODES.spain:
    case COUNTRY_CODES.usa:
    case COUNTRY_CODES.uk: {
      return {
        ...INIT_DISPLAY_FIELDS,
        isChildrenFieldVisible: true,
      };
    }
    default: {
      return INIT_DISPLAY_FIELDS;
    }
  }
}

function getAgeAboveAdults(reservation: any = {}, t: TFunction) {
  const countryCode = getHousingCountryCode(reservation);
  switch (countryCode) {
    case COUNTRY_CODES.italy:
      return undefined;
    default:
      return t('age_above');
  }
}

function getMinimumChildrenRestrictionAge(reservation: any = {}) {
  const countryCode = getHousingCountryCode(reservation);
  return MIN_CHILDREN_RESTRICTION_AGE[countryCode] || '';
}

type ConfirmBookingCounterProps = {
  title: string;
  subtitle?: string;
  value: number;
  onIncrement: () => void;
  onDecrement: () => void;
  error?: string;
};

function ConfirmBookingCounter({
  title,
  subtitle,
  value,
  onIncrement,
  onDecrement,
  error,
}: ConfirmBookingCounterProps) {
  return (
    <div className={styles.box}>
      <div className={styles.details}>
        <div className={styles.category}>{title}</div>
        <div className={styles.text}>{subtitle}</div>
      </div>

      <div>
        <Counter
          className={styles.counter}
          value={value}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
        />

        {error && <div className={styles.errorText}>{error}</div>}
      </div>
    </div>
  );
}

function ConfirmBookingDetailsForm() {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation<FormTypes | any>();
  const {reservation, isReservationLoaded} = useComputedReservationDetails();
  const {hasBiomatchForGuestLeader, hasBiomatchForAllGuests} =
    useComputedReservationDetails();
  const [displayFields, setDisplayFields] = React.useState(() => {
    return getDisplayFields(reservation);
  });
  const initChildrenNumber = getInitChildren(location);
  const {register, handleSubmit, watch, setValue, trigger, formState} =
    useForm<FormTypes>({
      defaultValues: {
        [FORM_NAMES.numberOfGuests]:
          getPersistedNumberOfGuests(location) ||
          getNumberOfGuests(reservation, MIN_INPUT_NUMBERS.number_of_guests),
        [FORM_NAMES.children]: initChildrenNumber,
        [FORM_NAMES.timeArrival]: HOURS24[30],
        [FORM_NAMES.timeLeave]: HOURS24[24],
      },
      shouldFocusError: false,
    });
  const {errors} = formState;

  const numberOfGuestsLabel = displayFields.isChildrenFieldVisible
    ? t('adults')
    : t(FORM_NAMES.numberOfGuests);

  const {IdGuestAppEvents} = useMixPanel();

  React.useEffect(() => {
    const displayFields = getDisplayFields(reservation);
    setDisplayFields(displayFields);

    setValue(FORM_NAMES.numberOfGuests, getNumberOfGuests(reservation));
  }, [reservation, setValue]);

  React.useEffect(
    function preloadTimes() {
      if (!isReservationLoaded) return;

      const arrivalDate = moment(
        new Date(location.state?.updatedCheckInDate || reservation.check_in_date),
      );
      const leaveDate = moment(
        new Date(location.state?.updatedCheckOutDate || reservation.check_out_date),
      );

      const arrivalTimeHour = arrivalDate.hour();
      const arrivalTimeMinute = arrivalDate.minute();
      const leaveTimeHour = leaveDate.hour();
      const leaveTimeMinute = leaveDate.minute();

      const arrivalTimeOption = HOURS24.find(
        ({value: {h, m}}) => h === arrivalTimeHour && m === arrivalTimeMinute,
      );
      const leaveTimeOption = HOURS24.find(
        ({value: {h, m}}) => h === leaveTimeHour && m === leaveTimeMinute,
      );

      setValue(FORM_NAMES.timeArrival, arrivalTimeOption || HOURS24[30]);
      setValue(FORM_NAMES.timeLeave, leaveTimeOption || HOURS24[24]);
    },
    [
      isReservationLoaded,
      location.state?.updatedCheckInDate,
      location.state?.updatedCheckOutDate,
      reservation,
      setValue,
    ],
  );

  React.useEffect(() => {
    if (displayFields.isAdultsFieldVisible) {
      register(FORM_NAMES.numberOfGuests, {
        required: t('required') as string,
        validate: value => {
          if (Number(value) < MIN_INPUT_NUMBERS.number_of_guests) {
            return t<string>('min_number_is', {
              number: MIN_INPUT_NUMBERS.number_of_guests,
            });
          }
          if (Number(value) > MAX_INPUT_NUMBERS.number_of_guests) {
            return t<string>('max_number_is', {
              number: MAX_INPUT_NUMBERS.number_of_guests,
            });
          }
        },
      });
    }

    if (displayFields.isChildrenFieldVisible) {
      register(FORM_NAMES.children, {
        required: t('required') as string,
        validate: value => {
          if (Number(value) < MIN_INPUT_NUMBERS.children) {
            return t<string>('min_number_is', {
              number: MIN_INPUT_NUMBERS.children,
            });
          }
          if (Number(value) > MAX_INPUT_NUMBERS.children) {
            return t<string>('max_number_is', {
              number: MAX_INPUT_NUMBERS.children,
            });
          }
        },
      });
      setValue(FORM_NAMES.children, initChildrenNumber);
    }
  }, [register, t, displayFields, setValue, initChildrenNumber]);

  const revalidateFormField = async (name?: FORM_NAMES) => {
    if (formState.isSubmitted) {
      await trigger(name);
    }
  };

  const handleInputDecrement = async (name: FORM_NAMES) => {
    const value = watch(name);
    const minNumber = MIN_INPUT_NUMBERS[name] || 0;

    if (Number(value) - 1 < minNumber) {
      setValue(name, 0);
      await revalidateFormField();
      return;
    }

    setValue(name, Number(watch(name)) - 1);
    await revalidateFormField();
  };

  const handleInputIncrement = async (name: FORM_NAMES) => {
    const value = watch(name);
    const minNumber = MIN_INPUT_NUMBERS[name] || 0;
    const maxNumber = MAX_INPUT_NUMBERS[name];

    if (Number(value) + 1 < minNumber) {
      setValue(name, 0);
      await revalidateFormField();
      return;
    }

    if (Number(value) + 1 > maxNumber) {
      setValue(name, maxNumber);
      await revalidateFormField();
      return;
    }

    setValue(name, Number(watch(name)) + 1);
    await revalidateFormField();
  };

  const getPersistedState = React.useCallback(
    (payload: FormTypes) => {
      // -------------------- FUTURE FEATUR -----------------
      // const {value: arrivalTimeValue} = payload[FORM_NAMES.timeArrival];
      // const {value: leaveTimeValue} = payload[FORM_NAMES.timeLeave];
      // const updatedCheckInDate = moment(new Date(reservation.check_in_date))
      //   .hour(arrivalTimeValue.h)
      //   .minute(arrivalTimeValue.m)
      //   .format();
      // const updatedCheckOutDate = moment(new Date(reservation.check_out_date))
      //   .hour(leaveTimeValue.h)
      //   .minute(leaveTimeValue.m)
      //   .format();
      // -------------------- FUTURE FEATUR -----------------

      return {
        ...location.state,
        // updatedCheckInDate,
        // updatedCheckOutDate,
        ...payload,
      };
    },
    [location.state],
  );

  const onSubmit = React.useCallback(
    (data: FormTypes) => {
      const state = getPersistedState(data);
      IdGuestAppEvents(GUEST_APP_EVENTS_NAME.confirmed);
      if (hasBiomatchForGuestLeader || hasBiomatchForAllGuests) {
        return history.push(PATHS.verificationStart, state);
      }
      history.push(PATHS.formPersonalData, state);
    },
    [
      getPersistedState,
      hasBiomatchForGuestLeader,
      hasBiomatchForAllGuests,
      history,
      IdGuestAppEvents,
    ],
  );

  React.useLayoutEffect(
    function handleIsFromHousingFlow() {
      const shouldSkipConfirmScreen =
        location?.state?.fromHousingFlow && !displayFields.isChildrenFieldVisible;

      if (shouldSkipConfirmScreen) {
        handleSubmit(onSubmit)();
      }
    },
    [
      handleSubmit,
      location,
      location.state,
      onSubmit,
      displayFields.isChildrenFieldVisible,
    ],
  );

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      {/*------------------ FUTURE FEATURE ------------------------*/}
      {/*<div className={styles.dropdownContainer}>*/}
      {/*  <Controller*/}
      {/*    name={FORM_NAMES.timeArrival}*/}
      {/*    rules={{required: true}}*/}
      {/*    control={control}*/}
      {/*    render={({field, fieldState: {error}}) => (*/}
      {/*      <Select*/}
      {/*        options={HOURS24}*/}
      {/*        error={error?.message}*/}
      {/*        label={t('arrival_time')}*/}
      {/*        {...field}*/}
      {/*      />*/}
      {/*    )}*/}
      {/*  />*/}
      {/*  <Controller*/}
      {/*    name={FORM_NAMES.timeLeave}*/}
      {/*    rules={{required: true}}*/}
      {/*    control={control}*/}
      {/*    render={({field, fieldState: {error}}) => (*/}
      {/*      <Select*/}
      {/*        options={HOURS24}*/}
      {/*        error={error?.message}*/}
      {/*        label={t('leaving_time')}*/}
      {/*        {...field}*/}
      {/*      />*/}
      {/*    )}*/}
      {/*  />*/}
      {/*  /!* <Dropdown*/}
      {/*    className={styles.dropdown}*/}
      {/*    options={dropdownOptions}*/}
      {/*    value={timeLeave}*/}
      {/*    setValue={setTimeLeave}*/}
      {/*    label="LEAVING TIME"*/}
      {/*  /> *!/*/}
      {/*</div>*/}
      {/*------------------ FUTURE FEATURE ------------------------*/}
      <div className={styles.counterContainer}>
        {displayFields.isAdultsFieldVisible && !location?.state?.fromHousingFlow && (
          <ConfirmBookingCounter
            title={numberOfGuestsLabel}
            subtitle={getAgeAboveAdults(reservation, t)}
            onDecrement={() => handleInputDecrement(FORM_NAMES.numberOfGuests)}
            onIncrement={() => handleInputIncrement(FORM_NAMES.numberOfGuests)}
            value={watch(FORM_NAMES.numberOfGuests)}
            error={errors?.[FORM_NAMES.numberOfGuests]?.message}
          />
        )}

        {displayFields.isChildrenFieldVisible && (
          <ConfirmBookingCounter
            title={t('children')}
            subtitle={t('under_number', {
              number: getMinimumChildrenRestrictionAge(reservation),
            })}
            onDecrement={() => handleInputDecrement(FORM_NAMES.children)}
            onIncrement={() => handleInputIncrement(FORM_NAMES.children)}
            value={
              watch(FORM_NAMES.children) === undefined
                ? initChildrenNumber
                : watch(FORM_NAMES.children)
            }
            error={errors?.children?.message as string}
          />
        )}
      </div>

      <div className={styles.bookingDetailsSubmitMobile}>
        <div className={styles.progress}>1/5</div>
        <Button className={styles.submitButton} type="submit" rightIcon="arrow-next">
          {t('confirm_details')}
        </Button>
      </div>
    </form>
  );
}

export {ConfirmBookingDetailsForm, getFormattedDateOrFallback, MIN_INPUT_NUMBERS};
